import React, { useState, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { ChevronBackIc, ChevronForwardIc } from '@dsch/dd-icons';

type Variant = 'DEFAULT' | 'COMPACT';

export interface BreadCrumb {
  path?: string;
  displayName: string;
  isNotLink?: boolean;
}

export interface BreadCrumbsProps {
  /** Breadcrumbs is used to populate the breadcrumb component */
  breadcrumbs: BreadCrumb[];
  baseUrl: string;
  variant?: Variant;
}

const BreadCrumbContainer = styled.div<{ variant: Variant }>`
  position: relative;
  display: flex;
  align-items: center;
  height: ${({ theme, variant }) =>
    variant === 'DEFAULT' ? theme.spacing.L40 : 'auto'};
`;

export const BreadCrumbList = styled.div`
  position: relative;
  overflow: auto;
  display: flex;
  align-items: center;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  /* Chrome/Safari/Webkit */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BackArrowContainer = styled.div`
  width: 24px;
  height: 24px;
  margin-left: -0.5rem; // added to compensate for extra space in icon
`;

const ForwardArrowContainer = styled.div`
  color: ${({ theme }) => theme.colors.GREY};
  margin: 0 6px;
`;

const Back = styled.div`
  display: none;
  @media (min-width: 833px) {
    display: block;
  }
`;

const BackButton = styled.button`
  ${({ theme }) => theme.fontSize.S12}
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  padding: 0 ${({ theme }) => theme.spacing.S8} 0 0;
  color: ${({ theme }) => theme.colors.BLUE};
  display: flex;
  align-items: center;
  margin: auto 0;
  cursor: pointer;

  @media (min-width: 833px) {
    padding-right: ${({ theme }) => theme.spacing.M24};
  }
`;

const BreadCrumbLink = styled.a`
  ${({ theme }) => theme.fontSize.S12}
  display: flex;
  align-items: center;
  margin: auto 0;

  white-space: nowrap;

  &:last-child {
    padding-right: ${({ theme }) => theme.spacing.M16};
  }
`;

const BreadCrumb = styled.p`
  ${({ theme }) => theme.fontSize.S12}
  display: flex;
  align-items: center;
  margin: auto 0;
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  white-space: nowrap;

  &:last-child {
    padding-right: ${({ theme }) => theme.spacing.M16};
  }
`;

export const StyledSpan = styled.span`
  display: block;
  position: absolute;
  width: 30px;
  left: ${({ theme }) => theme.spacing.M24};
  top: 0;
  bottom: 0;
  background: linear-gradient(
    270deg,
    rgba(242, 242, 242, 0.0001) 2.82%,
    ${({ theme }) => theme.colors.GREY_LIGHTER} 85.37%
  );

  z-index: 1;

  @media (min-width: 833px) {
    left: 67px;
  }
`;

function BreadCrumbs({
  breadcrumbs,
  baseUrl,
  variant = 'DEFAULT',
}: BreadCrumbsProps) {
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const theme = useTheme();

  const measuredRef = useCallback((element: HTMLDivElement) => {
    if (element !== null) {
      setIsContentOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, []);

  return (
    <BreadCrumbContainer variant={variant}>
      <BackButton onClick={() => window.history.back()}>
        <BackArrowContainer>
          <ChevronBackIc />
        </BackArrowContainer>
        <Back>Back</Back>
      </BackButton>
      {isContentOverflowing && <StyledSpan></StyledSpan>}

      <BreadCrumbList ref={measuredRef}>
        {breadcrumbs.map(({ displayName, path, isNotLink }, index) =>
          !isNotLink ? (
            <BreadCrumbLink
              href={`${baseUrl}${path}`}
              key={index}
              data-testid={`${displayName}-breadcrumb`}
            >
              {displayName}
              {breadcrumbs.length - 1 !== index && (
                <ForwardArrowContainer>
                  <ChevronForwardIc
                    width="12"
                    height="12"
                    color={theme.colors.GREY}
                  />
                </ForwardArrowContainer>
              )}
            </BreadCrumbLink>
          ) : (
            <BreadCrumb key={index}>
              {displayName}
              {breadcrumbs.length - 1 !== index && (
                <ForwardArrowContainer>
                  <ChevronForwardIc
                    width="12"
                    height="12"
                    color={theme.colors.GREY}
                  />
                </ForwardArrowContainer>
              )}
            </BreadCrumb>
          ),
        )}
      </BreadCrumbList>
    </BreadCrumbContainer>
  );
}

export { BreadCrumbs };
